import {
  DataTableProviderFilterItem,
  DataTableProviderFilterStateValue,
  DataTableProviderProps
} from './types';
import Card, { CardBody } from '../../ui/Card';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import DataTable from '../../ui/DataTable';
import SelectField from '../../ui/SelectField';
import {
  Button,
  ArrowLeftRightIcon,
  SearchInput,
  NavigationActionBar,
  Dropdown,
  useOnClickOutside,
  EllipsisVerticalIcon,
  IconButtonModes,
  IconButtonVariants,
  ButtonSizes,
  Drawer,
  DrawerStage,
  XIcon,
  RotateCcwIcon,
  XCircleIcon,
  FilterIcon,
  IconButton,
  useDrawerContext
} from 'stubben-edge-unified-frontend-react-components';
import { DataTableSortData } from '@ui/DataTable/types';
import IconFileReview from '@icons/IconFileReview';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { renderFilterDrawerGrid } from './datatableFilterDrawerFields';
import { appliedFiltersFingerprint } from './appliedFiltersFingerprint';
import {
  parseFilterQueryParams,
  writeDatatableFiltersToUrl
} from './syncFiltersToSearchParams';
import {
  useDatatableFilterDraftHandlers,
  postFilterChangeDependants
} from './useDatatableFilterDraftHandlers';
import { withPipelineContextFilter } from '@/components/common/TasksListComponent/pipelineContextFilters';
import SavedFiltersDrawerSection from './SavedFiltersDrawerSection';
import type { SavedFiltersDrawerSectionHandle } from './SavedFiltersDrawerSection';
import { DATATABLE_FILTER_DRAWER } from '@/constants/drawers';

// Session storage utilities
interface SerializedFilterState {
  filters: Array<[string, DataTableProviderFilterStateValue]>;
  searchTerm: string;
  sort: DataTableSortData | null;
}

/**
 * Generates a unique storage key for the datatable state.
 * Uses tableId if provided, otherwise falls back to pathname + keyField + title hash.
 */
const getStorageKey = (
  pathname: string,
  keyField: string,
  tableId?: string,
  title?: string
): string => {
  const pathnamePart = pathname.replaceAll('/', '_');

  if (tableId) {
    // Use explicit tableId for maximum clarity
    return `datatable_state${pathnamePart}_${tableId}`;
  }

  // Generate a unique suffix from available props
  const titleHash = title ? `_${title.toLowerCase().replace(/\s+/g, '-')}` : '';
  return `datatable_state${pathnamePart}_${keyField}${titleHash}`;
};

const saveToSessionStorage = (
  key: string,
  filters: Map<string, DataTableProviderFilterStateValue>,
  searchTerm: string,
  sort: DataTableSortData | null | undefined
): void => {
  try {
    const data: SerializedFilterState = {
      filters: Array.from(filters.entries()),
      searchTerm,
      sort: sort || null
    };
    sessionStorage.setItem(key, JSON.stringify(data));
  } catch (error) {
    console.warn('Failed to save datatable state to session storage:', error);
  }
};

const loadFromSessionStorage = (key: string): SerializedFilterState | null => {
  try {
    const data = sessionStorage.getItem(key);
    if (!data) return null;
    const parsed = JSON.parse(data) as SerializedFilterState;
    return parsed;
  } catch (error) {
    console.warn('Failed to load datatable state from session storage:', error);
    return null;
  }
};

const clearSessionStorage = (key: string): void => {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    console.warn(
      'Failed to clear datatable state from session storage:',
      error
    );
  }
};

/** Mirrors datatable persistence `useEffect`; call synchronously on apply/remove so unmount beats passive effects. */
function persistDatatableSessionSnapshot(
  key: string,
  filters: Map<string, DataTableProviderFilterStateValue>,
  searchTerm: string,
  sort: DataTableSortData | null | undefined
): void {
  if (filters.size > 0 || searchTerm !== '' || sort) {
    saveToSessionStorage(key, filters, searchTerm, sort);
  } else {
    clearSessionStorage(key);
  }
}

export default function DataTableProvider<T>(props: DataTableProviderProps<T>) {
  const {
    columns,
    column2,
    fetchResolver,
    onFetchComplete,
    onDataLoadingChange,
    onActiveFiltersChange,
    syncFiltersToUrl = false,
    paginationProps,
    actions,
    bulkActions,
    bulkActionsAvailability,
    extraActions,
    navigationActionBarClassName,
    navigationActionBarFilterButtonTestIdentifier,
    navigationActionBarSearchTestIdentifier,
    navigationActionBarBulkDropdownTestIdentifier,
    keyField = 'id',
    uniqueKeyField = '',
    searchable,
    composable,
    searchPlaceholder,
    filtersList,
    filtersTitleTestId,
    filterDrawerTitle,
    showDrawerResetFilters,
    selectAllHeaderTestId,
    actionsHeaderTestId,
    rowSelectTestIdentifier,
    onRowClicked,
    onCellClick,
    defaultSort,
    defaultFilter,
    contextPipeline,
    sanitizeSessionFilters,
    savedFilterSection,
    savedFilterContextId,
    emptyState,
    outdatedTimestamp,
    isSeparated,
    noResultsText,
    appendAfterRow,
    isResponsive,
    showPagination,
    showBulkError,
    bulkErrorText,
    handleBulkErrorClick,
    itemsToArchiveCount,
    isFixedLayout,
    showTitle,
    title = '',
    subtitle = '',
    refreshTime,
    useNewActionbar = false,
    navigationActionBarTitle,
    filled = false,
    gcTime = 1000 * 60 * 30, // 30 minutes
    tableId // tableId is the unique identifier for the data table and is used to store the state in session storage. It should be unique for each data table and should be snake_case.
  } = props;

  const location = useLocation();

  // Generate unique storage key for this table
  const storageKey = useMemo(
    () => getStorageKey(location.pathname, keyField as string, tableId, title),
    [location.pathname, keyField, tableId, title]
  );

  /** Stable prefix for E2E: `datatable-tasks-search`, `datatable-policies-bulk-apply`, etc. */
  const dataTableTestPrefix = useMemo(() => {
    if (tableId) {
      return `datatable-${String(tableId).replace(/_/g, '-')}`;
    }
    const routeSegment =
      location.pathname
        .replace(/^\//, '')
        .replace(/\//g, '-')
        .replace(/_/g, '-') || 'root';
    return `datatable-${routeSegment}`;
  }, [location.pathname, tableId]);

  const getFilterTestIdentifier = useCallback(
    (filterKey: string) =>
      `${dataTableTestPrefix}-filter-${filterKey.replace(/[_.]/g, '-')}`,
    [dataTableTestPrefix]
  );

  const resolveRowSelectTestIdentifier = useCallback(
    (row: T) =>
      rowSelectTestIdentifier
        ? rowSelectTestIdentifier(row)
        : `${dataTableTestPrefix}-row-select-${String(row[keyField as keyof T])}`,
    [rowSelectTestIdentifier, dataTableTestPrefix, keyField]
  );

  const resolvedSelectAllHeaderTestId =
    selectAllHeaderTestId ?? `${dataTableTestPrefix}-select-all`;

  const resolvedBulkErrorDismissTestId = `${dataTableTestPrefix}-bulk-error-dismiss`;

  const resolvedActionsHeaderTestId = actions
    ? (actionsHeaderTestId ?? `${dataTableTestPrefix}-actions-header`)
    : undefined;

  // Load initial state from session storage if available
  const initialState = useMemo(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const hasUrlParams = searchParams.toString().length > 0;

    // If URL params exist, don't load from session storage (URL takes precedence)
    if (hasUrlParams || defaultFilter) {
      return null;
    }

    // Try to load from session storage
    return loadFromSessionStorage(storageKey);
  }, [storageKey]);

  // Data state
  // const [items, setItems] = useState<Array<T>>([]);
  const {
    data,
    refetch,
    isLoading: loading,
    isFetching
  } = useQuery({
    queryKey: ['datatable', location.pathname, keyField, tableId || title],
    queryFn: () => fetchItems(),
    enabled: false,
    gcTime: gcTime
  });
  const items = data?.items || [];
  const isDataBusy = loading || isFetching;

  useEffect(() => {
    onDataLoadingChange?.(isDataBusy);
    return () => {
      onDataLoadingChange?.(false);
    };
  }, [isDataBusy, onDataLoadingChange]);

  // Pagination state
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Bulk state
  const [selected, setSelected] = useState<Array<T>>([]);
  useEffect(() => {
    setSelected([]);
  }, [showBulkError]);
  const [selectedBulkAction, setSelectedBulkAction] = useState<string | null>(
    null
  );
  const [bulkLoading, setBulkLoading] = useState(false);
  const disabledBulkIds = bulkActionsAvailability
    ? items
        .filter((i) => !bulkActionsAvailability(i))
        .map((i) => i[keyField as keyof T])
    : [];

  // Search state - initialize from session storage if available
  const [searchTerm, setSearchTerm] = useState(initialState?.searchTerm || '');
  const searchTermRef = useRef(searchTerm);
  searchTermRef.current = searchTerm;

  const filterKeys = useMemo(
    () => filtersList?.map((item) => item.filterKey) ?? [],
    [filtersList]
  );

  const writeFiltersToUrl = useCallback(
    (
      filters: Map<string, DataTableProviderFilterStateValue>,
      nextSearchTerm = searchTermRef.current
    ) => {
      if (!syncFiltersToUrl) return;
      writeDatatableFiltersToUrl({
        filters,
        searchTerm: nextSearchTerm,
        filterKeys,
        pathname: location.pathname
      });
    },
    [syncFiltersToUrl, filterKeys, location.pathname]
  );

  const [searchTimerId, setSearchTimerId] = useState<number>();

  // Sorting state - initialize from session storage if available
  const [sort, setSort] = useState<DataTableSortData | null | undefined>(
    initialState?.sort || defaultSort || null
  );

  // Filters drawer via DS context — unique name per table instance
  const drawerCtx = useDrawerContext();
  const filterDrawerName = `${DATATABLE_FILTER_DRAWER}-${dataTableTestPrefix}`;
  const savedFiltersRef = useRef<SavedFiltersDrawerSectionHandle | null>(null);

  // Mobile dropdown state
  const dropdownWrapperRef = useRef<HTMLDivElement | null>(null);
  const dropdownButtonRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleDropdownClickOutside = useCallback(
    () => setDropdownOpen(false),
    []
  );

  useOnClickOutside<HTMLDivElement>(
    [dropdownWrapperRef, dropdownButtonRef, dropdownRef],
    handleDropdownClickOutside
  );
  const [filtersState, setFiltersState] = useState<
    Map<string, DataTableProviderFilterStateValue>
  >(initialState ? new Map(initialState.filters) : defaultFilter ?? new Map());
  const [activeFilters, setActiveFilters] = useState<
    Map<string, DataTableProviderFilterStateValue>
  >(initialState ? new Map(initialState.filters) : defaultFilter ?? new Map());

  const [newFilters, setNewFilters] = useState<
    Map<string, DataTableProviderFilterStateValue>
  >(initialState ? new Map(initialState.filters) : defaultFilter ?? new Map());

  const {
    handleFilterInputChange,
    handleFilterSelectChange,
    handleAutoCompleteChange
  } = useDatatableFilterDraftHandlers(filtersState, setFiltersState);

  const [newFetchCompletionTimestamp, setNewFetchCompletionTimestamp] =
    useState<string>();

  // Sort drawer state
  const [sortOpen, setSortOpen] = useState(false);

  // Save state whenever filters/search/sort change (also persists synchronously from apply/remove/reset handlers)
  useEffect(() => {
    persistDatatableSessionSnapshot(
      storageKey,
      activeFilters,
      searchTerm,
      sort
    );
  }, [activeFilters, searchTerm, sort, storageKey]);

  useEffect(() => {
    onActiveFiltersChange?.(activeFilters);
  }, [activeFilters, onActiveFiltersChange]);

  // Initialize filters from URL parameters on mount
  useEffect(() => {
    // Don't override if defaultFilter is already set
    if (defaultFilter && defaultFilter.size > 0) return;
    if (!filtersList || filtersList.length === 0) return;

    const searchParams = new URLSearchParams(window.location.search);

    // Async function to fetch filter labels
    const initializeFilters = async () => {
      const urlFilters = new Map<string, DataTableProviderFilterStateValue>();

      // Process filters sequentially to handle dependencies (e.g., pipeline_stage depends on pipeline)
      for (const filterItem of filtersList) {
        const urlValue = searchParams.get(filterItem.filterKey);
        if (urlValue) {
          // Handle date_range and other array-based filters (comma-separated values)
          let value: string | string[] | number = urlValue;
          let label: string | string[] | number = urlValue;

          if (filterItem.type === 'date_range') {
            value = urlValue.split(',');
            label = value;
          } else if (filterItem.type === 'number') {
            value = Number(urlValue);
            label = urlValue;
          } else if (filterItem.type === 'autocomplete') {
            // For autocomplete filters, try to fetch the label using itemResolver
            try {
              const autoCompleteOptions =
                filterItem.autoCompleteOptions?.(urlFilters);
              if (autoCompleteOptions?.itemResolver) {
                const item = await autoCompleteOptions.itemResolver(urlValue);
                if (item) {
                  label = item.label;
                }
              }
            } catch (error) {
              console.warn(
                `Failed to fetch label for ${filterItem.filterKey}:`,
                error
              );
              // Keep the value as label if fetch fails
            }
          } else if (
            filterItem.type === 'select' ||
            filterItem.type === 'choice_chips'
          ) {
            // Try to find a label from select options
            if (filterItem.selectOptions) {
              const option = filterItem.selectOptions.find(
                (opt) => String(opt.value) === urlValue
              );
              if (option) {
                label = String(option.label);
              }
            } else if (filterItem.selectOptionsResolver) {
              // Fetch options asynchronously if resolver is provided
              try {
                const options = await filterItem.selectOptionsResolver();
                const option = options?.find(
                  (opt) => String(opt.value) === urlValue
                );
                if (option) {
                  label = String(option.label);
                }
              } catch (error) {
                console.warn(
                  `Failed to fetch select options for ${filterItem.filterKey}:`,
                  error
                );
              }
            }
          } else {
            // For other types, keep the value as label
            label = urlValue;
          }

          urlFilters.set(filterItem.filterKey, {
            value: value,
            label: label,
            item: filterItem
          });
        }
      }

      // Only update if we found filters in the URL
      if (urlFilters.size > 0) {
        setFiltersState(urlFilters);
        setActiveFilters(urlFilters);
        setNewFilters(urlFilters);
      }
    };

    // Check for search term in URL (common parameter names)
    const searchValue = searchParams.get('search') || searchParams.get('q');
    if (searchValue) {
      setSearchTerm(searchValue);
    }

    // Execute async initialization
    initializeFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only on mount

  const fetchItems = async (overridePage?: number) => {
    const filtersObj = parseFilterQueryParams(window.location.search);

    if (filtersList && newFilters?.size) {
      newFilters.forEach((state, key) => {
        filtersObj[key] = state.value;
      });
    }

    return fetchResolver({
      page: overridePage ?? page,
      perPage,
      searchTerm: searchTerm && searchTerm !== '' ? searchTerm : undefined,
      sort: sort ?? undefined,
      filters: filtersObj
    })
      .then((data) => {
        // setItems(data.items);
        setSelected([]);
        setSelectedBulkAction(null);
        if (Number(data.pagination.page) !== Number(page))
          setPage(Number(data.pagination.page));
        if (Number(data.pagination.perPage) !== Number(perPage))
          setPerPage(Number(data.pagination.perPage));
        if (Number(data.pagination.total) !== Number(total))
          setTotal(Number(data.pagination.total));
        onFetchComplete?.(data);
        return data;
      })
      .catch((e) => {
        console.error('Datatable Error:', e);
      })
      .finally(() => {
        setNewFetchCompletionTimestamp(new Date().toISOString());
      });
  };

  useEffect(() => {
    if (refreshTime && newFetchCompletionTimestamp && !loading) {
      const interval = setTimeout(() => {
        refetch();
      }, refreshTime);
      return () => clearTimeout(interval);
    }
  }, [newFetchCompletionTimestamp]);

  useEffect(() => {
    refetch();
  }, [newFilters, outdatedTimestamp, refreshTime, sort]);

  const bulkActionOptions = bulkActions?.map((b, index) => ({
    value: b.label,
    icon: b.icon,
    label: b.label
  }));

  const handleBulkActionTrigger = async () => {
    if (!selected || selected.length === 0) {
      alert('No items selected');
      return;
    }
    const theBulkAction = bulkActions?.find(
      (b) => b.label === selectedBulkAction
    );
    if (!theBulkAction) return;
    setBulkLoading(true);
    const status = await theBulkAction.action(selected);
    if (status) {
      // fetchItems().then();
      refetch();
      setSelected([]);
    }
    setBulkLoading(false);
  };

  const handleApplyFilter = (resetPage = true) => {
    if (resetPage) {
      setPage(1);
    }
    const newFilters = new Map<string, DataTableProviderFilterStateValue>();
    filtersState?.forEach((state, key, i) => {
      if (state && state.value && state.value !== '')
        newFilters.set(key, state);
    });
    const applied = sanitizeSessionFilters
      ? sanitizeSessionFilters(newFilters)
      : newFilters;
    setNewFilters(applied);
    setActiveFilters(applied);
    drawerCtx.close(filterDrawerName);
    persistDatatableSessionSnapshot(storageKey, applied, searchTermRef.current, sort);
    writeFiltersToUrl(applied);
  };

  // To minimise calls to api force a 2 seconds timeout after the user has stopped typing.
  const handleUpdateSearch = (searchValue: string) => {
    searchTermRef.current = searchValue;
    setSearchTerm(searchValue);
    if (searchTimerId) {
      clearTimeout(searchTimerId);
    }
    const timer = window.setTimeout(handleApplyFilter, 2000);
    setSearchTimerId(timer);
  };

  const handleSortChange = (sortedEntity: DataTableSortData) => {
    setSort(sortedEntity);
    setPage(1);
    persistDatatableSessionSnapshot(
      storageKey,
      activeFilters ?? new Map(),
      searchTerm,
      sortedEntity
    );
  };

  const handleChangePage = (page: number) => {
    setPage(page);
    handleApplyFilter(false);
  };

  const handleChangePerPage = (perPage: number) => {
    setPerPage(perPage);
    handleApplyFilter(false);
  };

  const handleRemoveFilter = (filterKey: string) => {
    if (!activeFilters?.has(filterKey)) return;
    const newFilters = new Map(activeFilters);
    newFilters.delete(filterKey);

    if (filtersState?.has(filterKey)) {
      const newFiltersState = new Map(filtersState);
      const relevantState = newFiltersState.get(filterKey);
      if (relevantState?.value) {
        relevantState.value = '';
        newFiltersState.set(filterKey, relevantState);
        if (relevantState.item) {
          // Reset dependant filters
          postFilterChangeDependants(relevantState.item, newFiltersState);
          if (relevantState.item.dependants) {
            relevantState.item.dependants.forEach((key) => {
              newFilters.delete(key);
            });
          }
        }
        setFiltersState(newFiltersState);
      }
    }

    if (syncFiltersToUrl) {
      writeFiltersToUrl(newFilters);
    } else if (window.location.search !== '') {
      const searchParams = new URLSearchParams(location.search);
      if (filterKey === 'pipeline_id') {
        searchParams.delete(filterKey);
        searchParams.delete('pipeline_stage_id');
      } else {
        searchParams.delete(filterKey);
      }
      const newUrl = `${location.pathname}?${searchParams.toString()}`;
      window.history.replaceState(null, '', newUrl);
    }

    setFiltersState(newFilters);
    setNewFilters(newFilters);
    setActiveFilters(newFilters);
    persistDatatableSessionSnapshot(storageKey, newFilters, searchTerm, sort);
  };

  const handleFilterReset = () => {
    const emptyFilters = new Map<string, DataTableProviderFilterStateValue>();
    searchTermRef.current = '';
    if (syncFiltersToUrl) {
      writeFiltersToUrl(emptyFilters, '');
    } else {
      window.history.replaceState({}, document.title, location.pathname);
    }

    setActiveFilters(emptyFilters);
    setNewFilters(emptyFilters);
    setFiltersState(emptyFilters);

    setSearchTerm('');
    setSort(null);

    persistDatatableSessionSnapshot(storageKey, emptyFilters, '', null);
  };

  const contextPipelineId = contextPipeline?.id;
  const contextPipelineName = contextPipeline?.name;

  useLayoutEffect(() => {
    if (!sanitizeSessionFilters) return;
    setActiveFilters((prev) => {
      const next = sanitizeSessionFilters(prev);
      if (appliedFiltersFingerprint(prev) === appliedFiltersFingerprint(next)) {
        return prev;
      }
      setFiltersState(next);
      setNewFilters(next);
      persistDatatableSessionSnapshot(storageKey, next, searchTerm, sort);
      return next;
    });
  }, [sanitizeSessionFilters, storageKey, searchTerm, sort]);

  const activeFiltersForUi = useMemo(() => {
    const m = new Map(activeFilters);
    m.delete('owner_uuid');
    if (
      contextPipelineId &&
      String(m.get('pipeline_id')?.value) === String(contextPipelineId)
    ) {
      m.delete('pipeline_id');
    }
    return m;
  }, [activeFilters, contextPipelineId]);

  const appliedFiltersSyncKey = useMemo(
    () => appliedFiltersFingerprint(activeFilters),
    [activeFilters]
  );

  const filtersDrawerOpen = drawerCtx.isOpen(filterDrawerName);

  useLayoutEffect(() => {
    if (filtersDrawerOpen) {
      setFiltersState(new Map(activeFilters));
    }
  }, [filtersDrawerOpen, activeFilters, appliedFiltersSyncKey]);

  useLayoutEffect(() => {
    if (!contextPipelineId) return;
    setActiveFilters((prev) => {
      if (prev.has('pipeline_id')) return prev;
      const next = withPipelineContextFilter(prev, {
        id: contextPipelineId,
        name: contextPipelineName ?? contextPipelineId
      });
      setFiltersState(next);
      setNewFilters(next);
      persistDatatableSessionSnapshot(storageKey, next, searchTerm, sort);
      return next;
    });
  }, [contextPipelineId, contextPipelineName, storageKey, searchTerm, sort]);

  const hasActiveFilters = activeFiltersForUi.size > 0;

  /** Default table sort is not “user-applied”; avoid showing Clear after reset when remounting (e.g. Board ↔ List). */
  const hasUserCustomSort = useMemo(() => {
    if (sort == null || sort === undefined) return false;
    if (defaultSort == null) return true;
    return (
      sort.field !== defaultSort.field ||
      sort.direction !== defaultSort.direction
    );
  }, [sort, defaultSort]);

  const isEmpty =
    emptyState && !loading && items.length === 0 && !hasActiveFilters;
  const hasActions =
    bulkActionOptions || searchable || filtersList || composable;
  const hasFilters = filtersList && !isEmpty;

  // Checkbox handlers for NavigationActionBar
  const allSelected = !isEmpty && selected.length === items.length;
  const someSelected = selected.length > 0 && selected.length < items.length;

  const handleCheckboxChange = (checked: boolean) => {
    if (checked) {
      // Select all items
      setSelected(items);
    } else {
      // Deselect all
      setSelected([]);
    }
  };

  const handleBulkActionSelect = async (value: string | number) => {
    if (!selected || selected.length === 0) {
      alert('No items selected');
      return;
    }
    const theBulkAction = bulkActions?.find((b) => b.label === value);
    if (!theBulkAction) return;
    setBulkLoading(true);
    const status = await theBulkAction.action(selected);
    if (status) {
      // fetchItems().then();
      refetch();
      setSelected([]);
    }
    setBulkLoading(false);
  };

  const showClearFiltersForToolbar =
    hasActiveFilters ||
    (!!searchTerm && searchTerm !== '') ||
    hasUserCustomSort;

  const actionSectionToolbarContext = {
    clearFiltersAndSearch: handleFilterReset,
    showClearFilters: showClearFiltersForToolbar
  };

  const resolvedExtraActions: ReactNode =
    typeof extraActions === 'function'
      ? extraActions(actionSectionToolbarContext)
      : extraActions;

  return (
    <>
      {(hasActions || hasFilters || showTitle) && (
        <div>
          {hasActions && useNewActionbar && (
            <NavigationActionBar
              filled={filled}
              className={navigationActionBarClassName}
              pageTitle={navigationActionBarTitle}
              showBulkSelection={Boolean(bulkActions?.length)}
              searchPlaceholder={searchPlaceholder || 'Search'}
              searchValue={searchTerm}
              onSearchChange={handleUpdateSearch}
              searchInputTestIdentifier={
                navigationActionBarSearchTestIdentifier
              }
              checkboxChecked={allSelected}
              checkboxIndeterminate={someSelected}
              onCheckboxChange={handleCheckboxChange}
              dropdownOptions={bulkActionOptions}
              onDropdownOptionSelect={handleBulkActionSelect}
              bulkDropdownDisabled={selected.length === 0}
              bulkDropdownButtonTestIdentifier={
                navigationActionBarBulkDropdownTestIdentifier
              }
              filterActive={hasActiveFilters}
              filterCount={hasFilters ? activeFiltersForUi.size : undefined}
              onFilterClick={
                hasFilters ? () => drawerCtx.open(filterDrawerName) : undefined
              }
              filterButtonTestIdentifier={
                navigationActionBarFilterButtonTestIdentifier
              }
              actionSection={resolvedExtraActions}
              testIdentifier={`${dataTableTestPrefix}-action-bar`}
            />
          )}
          {hasActions && !useNewActionbar && (
            <Card
              raised={false}
              bgColor={isSeparated ? 'default' : 'dimmer'}
              padding="dense"
              className="mb-4"
            >
              <CardBody className="mx-6 flex flex-col items-center justify-between gap-2 space-x-4 md:flex-row md:gap-0">
                <div className="flex flex-col items-center space-x-4 md:flex-row ">
                  <span className="h5">Actions</span>
                  {bulkActionOptions && !isEmpty && (
                    <span className="flex flex-col flex-nowrap items-center justify-start space-x-4 md:flex-row">
                      <SelectField
                        disabled={!selected?.length}
                        placeholder="Bulk actions"
                        value={selectedBulkAction || ''}
                        onChange={(s) => setSelectedBulkAction(s)}
                        options={bulkActionOptions}
                        selectProps={
                          {
                            'data-testid': `${dataTableTestPrefix}-bulk-actions-select`
                          } as any
                        }
                      />
                      <Button
                        size="sm"
                        variant="secondary"
                        loading={bulkLoading}
                        disabled={
                          !selected?.length || !selectedBulkAction || loading
                        }
                        onClick={handleBulkActionTrigger}
                        testIdentifier={`${dataTableTestPrefix}-bulk-apply`}
                      >
                        Apply
                      </Button>
                    </span>
                  )}
                </div>
                {searchable && !isEmpty && (
                  <div className="max-w-[221px]">
                    <SearchInput
                      placeholder={searchPlaceholder || 'Search'}
                      variant="secondary"
                      value={searchTerm}
                      size="sm"
                      onChange={(searchValue: string) =>
                        handleUpdateSearch(searchValue)
                      }
                      testIdentifier={`${dataTableTestPrefix}-search`}
                      inputProps={
                        {
                          'data-testid': `${dataTableTestPrefix}-search`
                        } as any
                      }
                    />
                  </div>
                )}
                {extraActions && (
                  <span className="flex flex-nowrap items-center justify-end space-x-4">
                    {resolvedExtraActions}
                  </span>
                )}
              </CardBody>
            </Card>
          )}
          {showTitle && (
            <Card
              padding="dense"
              raised={false}
              bgColor="default"
              className={`mt-4 duration-200 transition-opacity${
                loading ? ' pointer-events-none opacity-50' : ''
              }${hasFilters ? ' relative z-10 -mb-7' : ' mb-4'}`}
            >
              <CardBody>
                <div className="flex items-center py-2">
                  <IconFileReview className="mr-4 h-10 w-10 text-primary" />
                  <div>
                    <span className="h3 inline-block shrink-0">{title}</span>
                    {subtitle.length > 0 && (
                      <span className="mt-1 block">{subtitle}</span>
                    )}
                  </div>
                </div>
                {hasFilters && <hr className="mt-1" />}
              </CardBody>
            </Card>
          )}
          {hasFilters && (
            <>
              {!useNewActionbar && (
                <Card
                  padding="dense"
                  raised={false}
                  bgColor="default"
                  className={`mb-4 mt-4 border duration-200 transition-opacity${
                    loading ? ' pointer-events-none opacity-50' : ''
                  }`}
                >
                  <CardBody>
                    <div className="inline-flex w-full items-center justify-between">
                      <span
                        className="h5 mr-4 shrink-0"
                        {...(filtersTitleTestId
                          ? {
                              id: filtersTitleTestId,
                              name: filtersTitleTestId,
                              'data-testid': filtersTitleTestId
                            }
                          : {})}
                      >
                        Filters
                      </span>
                      <div className="flex-auto">
                        {hasActiveFilters && (
                          <div className="flex flex-auto flex-wrap items-center">
                            {Array.from(activeFiltersForUi).map(
                              ([key, filterState]) => (
                                <span
                                  key={key}
                                  className="my-0.5 mr-2 inline-flex items-center rounded-lg bg-primary-100 px-1 py-0.5 text-primary"
                                >
                                  <span className="inline-flex space-x-1 px-1 py-0.5 text-left text-xs leading-none">
                                    <span className="font-medium">
                                      {filterState.item?.label}:
                                    </span>
                                    <span className="font-semibold">
                                      {Array.isArray(filterState.label)
                                        ? `"${filterState.label.join('→')}"`
                                        : `"${filterState.label}"`}
                                    </span>
                                  </span>
                                  <IconButton
                                    size="sm"
                                    mode="ghost"
                                    testIdentifier={`${dataTableTestPrefix}-filter-${key.replace(
                                      /[_.]/g,
                                      '-'
                                    )}-remove`}
                                    onClick={() =>
                                      filterState.item &&
                                      handleRemoveFilter(
                                        filterState.item?.filterKey
                                      )
                                    }
                                  >
                                    <XIcon size={16} className="text-primary" />
                                  </IconButton>
                                </span>
                              )
                            )}
                          </div>
                        )}
                      </div>
                      {/* Desktop view */}
                      <div className="mr-6 hidden shrink-0 items-center gap-4 md:flex">
                        <Button
                          size="sm"
                          variant="secondary"
                          leftSlot={
                            hasActiveFilters ? <ArrowLeftRightIcon /> : <FilterIcon />
                          }
                          onClick={() => drawerCtx.open(filterDrawerName)}
                          testIdentifier={`${dataTableTestPrefix}-filters-open`}
                        >
                          {hasActiveFilters
                            ? 'Change filters'
                            : 'Select filters'}
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          leftSlot={<RotateCcwIcon />}
                          disabled={activeFiltersForUi.size === 0}
                          onClick={handleFilterReset}
                          testIdentifier={`${dataTableTestPrefix}-filters-reset`}
                        >
                          Reset filters
                        </Button>
                      </div>
                      {/* Mobile view */}
                      <div
                        ref={dropdownWrapperRef}
                        className="relative mr-6 flex shrink-0 items-center md:hidden"
                      >
                        <div ref={dropdownButtonRef}>
                          <IconButton
                            icon={<EllipsisVerticalIcon />}
                            mode={IconButtonModes.Ghost}
                            variant={IconButtonVariants.Tertiary}
                            size={ButtonSizes.SM}
                            onClick={() => setDropdownOpen(!dropdownOpen)}
                            testIdentifier={`${dataTableTestPrefix}-filters-mobile-menu`}
                          />
                        </div>
                        <div ref={dropdownRef}>
                          <Dropdown
                            open={dropdownOpen}
                            testIdentifier={`${dataTableTestPrefix}-filters-mobile-menu-dropdown`}
                            options={[
                              {
                                label: hasActiveFilters
                                  ? 'Change filters'
                                  : 'Select filters',
                                value: 'select-filters',
                                icon: hasActiveFilters ? (
                                  <ArrowLeftRightIcon />
                                ) : (
                                  <FilterIcon />
                                ),
                                href: undefined
                              },
                              {
                                label: 'Reset filters',
                                value: 'reset-filters',
                                icon: <RotateCcwIcon />,
                                href: undefined
                              }
                            ]}
                            handleSelectOption={(value) => {
                              setDropdownOpen(false);
                              if (value === 'select-filters') {
                                drawerCtx.open(filterDrawerName);
                              } else if (value === 'reset-filters') {
                                handleFilterReset();
                              }
                            }}
                            position="left"
                          />
                        </div>
                      </div>
                    </div>
                  </CardBody>
                </Card>
              )}
              {/* Drawer for filters */}
              <Drawer
                name={filterDrawerName}
                testIdentifier={`${dataTableTestPrefix}-filters-drawer`}
                onClose={() => {
                  setFiltersState(activeFilters);
                }}
              >
                <DrawerStage
                  title={filterDrawerTitle || 'Filters'}
                  leftSlot={<FilterIcon size={32} className="text-primary" />}
                  className="min-h-0 flex-1"
                  handleClose={() => {
                    setFiltersState(activeFilters);
                    drawerCtx.close(filterDrawerName);
                  }}
                  testIdentifier={`${dataTableTestPrefix}-filters-drawer-stage`}
                  actions={
                    <div className="flex w-full items-center justify-between">
                      {showDrawerResetFilters ? (
                        <Button
                          variant="tertiary"
                          mode="ghost"
                          leftSlot={<RotateCcwIcon />}
                          onClick={handleFilterReset}
                          testIdentifier={`${dataTableTestPrefix}-filters-drawer-reset`}
                        >
                          Reset filters
                        </Button>
                      ) : (
                        <Button
                          variant="secondary"
                          leftSlot={<XCircleIcon />}
                          onClick={() => drawerCtx.close(filterDrawerName)}
                          testIdentifier={`${dataTableTestPrefix}-filters-drawer-cancel`}
                        >
                          Cancel
                        </Button>
                      )}
                      <Button
                        onClick={async () => {
                          if (savedFilterSection && savedFiltersRef.current) {
                            const ok =
                              await savedFiltersRef.current.persistIfNeeded();
                            if (!ok) return;
                          }
                          handleApplyFilter();
                        }}
                        testIdentifier={`${dataTableTestPrefix}-filters-drawer-apply`}
                      >
                        Apply
                      </Button>
                    </div>
                  }
                >
                  {filtersList && (
                    <div key={appliedFiltersFingerprint(activeFilters)}>
                      {savedFilterSection ? (
                        <SavedFiltersDrawerSection
                          ref={savedFiltersRef}
                          section={savedFilterSection}
                          contextId={savedFilterContextId}
                          filtersList={filtersList}
                          filtersState={filtersState}
                          onHydrateFilters={setFiltersState}
                          onReset={handleFilterReset}
                          testIdentifierPrefix={`${dataTableTestPrefix}-saved-filters`}
                        >
                          {renderFilterDrawerGrid({
                            filtersList,
                            filtersState,
                            handleFilterInputChange,
                            handleFilterSelectChange,
                            handleAutoCompleteChange,
                            getFilterTestIdentifierFn: getFilterTestIdentifier
                          })}
                        </SavedFiltersDrawerSection>
                      ) : (
                        renderFilterDrawerGrid({
                          filtersList,
                          filtersState,
                          handleFilterInputChange,
                          handleFilterSelectChange,
                          handleAutoCompleteChange,
                          getFilterTestIdentifierFn: getFilterTestIdentifier
                        })
                      )}
                    </div>
                  )}
                </DrawerStage>
              </Drawer>
            </>
          )}
        </div>
      )}
      <DataTable
        columns={columns}
        data={items}
        count={total}
        itemsToArchiveCount={itemsToArchiveCount}
        bulkErrorText={bulkErrorText}
        showBulkError={showBulkError}
        handleBulkErrorClick={handleBulkErrorClick}
        bulkErrorDismissTestId={resolvedBulkErrorDismissTestId}
        page={page}
        onPageChange={(page) => handleChangePage(page)}
        rowsPerPage={perPage}
        onRowsPerPageChange={(perPage) => handleChangePerPage(perPage)}
        paginationProps={paginationProps}
        loading={loading}
        actions={actions}
        keyField={keyField}
        uniqueKeyField={uniqueKeyField}
        selected={selected}
        onSelect={bulkActions ? setSelected : undefined}
        disableSelectFor={
          disabledBulkIds.length > 0 ? disabledBulkIds : undefined
        }
        sort={sort}
        onSortChange={(sortedEntity) =>
          handleSortChange(sortedEntity as DataTableSortData)
        }
        onRowClicked={onRowClicked}
        onCellClick={onCellClick}
        emptyState={hasActiveFilters ? undefined : emptyState}
        hasActiveFilters={
          hasActiveFilters || (!!searchTerm && searchTerm !== '')
        }
        noResultsText={noResultsText}
        appendAfterRow={appendAfterRow}
        isResponsive={isResponsive}
        selectAllHeaderTestId={resolvedSelectAllHeaderTestId}
        rowSelectTestIdentifier={resolveRowSelectTestIdentifier}
        actionsHeaderTestId={resolvedActionsHeaderTestId}
        showPagination={showPagination}
        isFixedLayout={isFixedLayout}
      />
      {column2 && items.length > 0 && (
        <DataTable
          columns={column2}
          data={items}
          count={total}
          itemsToArchiveCount={itemsToArchiveCount}
          bulkErrorText={bulkErrorText}
          showBulkError={showBulkError}
          handleBulkErrorClick={handleBulkErrorClick}
          bulkErrorDismissTestId={resolvedBulkErrorDismissTestId}
          page={page}
          onPageChange={(page) => handleChangePage(page)}
          rowsPerPage={perPage}
          onRowsPerPageChange={(perPage) => handleChangePerPage(perPage)}
          paginationProps={paginationProps}
          loading={loading}
          actions={actions}
          keyField={keyField}
          uniqueKeyField={uniqueKeyField}
          selected={selected}
          onSelect={bulkActions ? setSelected : undefined}
          disableSelectFor={
            disabledBulkIds.length > 0 ? disabledBulkIds : undefined
          }
          sort={sort}
          onSortChange={(sortedEntity) =>
            handleSortChange(sortedEntity as DataTableSortData)
          }
          onRowClicked={onRowClicked}
          emptyState={hasActiveFilters ? undefined : emptyState}
          hasActiveFilters={
            hasActiveFilters || (!!searchTerm && searchTerm !== '')
          }
          noResultsText={noResultsText}
          appendAfterRow={appendAfterRow}
          isResponsive={isResponsive}
          selectAllHeaderTestId={`${dataTableTestPrefix}-select-all-secondary`}
          rowSelectTestIdentifier={resolveRowSelectTestIdentifier}
          actionsHeaderTestId={resolvedActionsHeaderTestId}
          showPagination={showPagination}
          isFixedLayout={isFixedLayout}
        />
      )}
    </>
  );
}
